"use strict";

/**
 * Returns 'Mobile' or 'Desktop' key if window variable exists.
 * If `checkTablet` is passed, additional media query is checked that
 * returns 'Tablet' if matched.
 */
window.getPlatform = ({
  checkTablet = false
} = {}) => {
  let platformKey = 'Desktop';
  if (typeof window !== 'undefined') {
    platformKey = 'Mobile';
    if (window.matchMedia('screen and (min-width: 1024px)').matches) {
      platformKey = 'Desktop';
    } else if (checkTablet && window.matchMedia('(min-width: 768px)').matches) {
      platformKey = 'Tablet';
    }
  }
  return platformKey;
};

/**
* Avoid multiple function execution on some repetitive actions
* like window resize or multiple clicks.
*/
window.debounce = (fn, delay) => {
  let timer = null;

  // eslint-disable-next-line func-names
  return function () {
    const context = this;
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
};
window.getTokenExpirationDate = token => JSON.parse(atob(token.split('.')[1])).exp * 1000;

/**
* Method for setting cookie to main domain (if subdomain)
* if address is IP value, return IP
*/
window.getSiteDomain = () => {
  const documentDomain = location.hostname;
  const documentDomainParts = documentDomain.split('.');
  return !documentDomainParts || documentDomainParts.length === 4 ? documentDomain : documentDomainParts.slice(-2).join('.');
};

/**
 * Refresh token and set it in cookies. If refresh fails,
 * removes user data from local storage and cookies.
 */
async function refreshUserToken(token) {
  const response = await fetch(`${window.baseApiUrl}/accounts/token/refresh`, {
    method: 'POST',
    body: JSON.stringify({
      token
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
  if (response.ok) {
    const data = await response.json();
    const expirationDate = window.getTokenExpirationDate(data.token);
    window.Cookies.set(window.jwtCookieKey, data.token, {
      expires: new Date(expirationDate),
      domain: window.getSiteDomain()
    });
  } else {
    localStorage.removeItem('profileData');
    window.Cookies.remove(window.jwtCookieKey, {
      domain: window.getSiteDomain()
    });
    window.showSnackbar('Tvoja prijava je istekla, molimo prijavi se ponovno.', 'warning');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }
}
;
function checkTokenValidity(token) {
  const expirationDate = window.getTokenExpirationDate(token);
  const expirationLeft = (expirationDate - new Date().getTime()) / (24 * 60 * 60 * 1000);
  if (expirationLeft < 1) {
    refreshUserToken(token);
  }
  return token;
}

// Try to get JWT token from cookies and cache it for future use
let cachedUserToken = null;
window.getUserToken = () => {
  if (cachedUserToken) return cachedUserToken;
  const token = window.Cookies.get(window.jwtCookieKey);
  if (token) {
    cachedUserToken = checkTokenValidity(token);
    return cachedUserToken;
  }
  return null;
};

/**
 * Return if the viewport is smaller than 1024px,
 * which suggests it is the mobile screen width.
 */
window.isMob = window.innerWidth < 1024;

/**
 * Get API settings data, deserialize it and prepare as key based object
 */
async function getApiSettings() {
  const apiSettings = {};
  const apiSettingsUrl = `${window.baseApiUrl}/settings/settings/`;
  try {
    const apiSettingsResponse = await fetch(apiSettingsUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/vnd.api+json'
      }
    });
    if (apiSettingsResponse.status === 200) {
      const apiSettingsData = await apiSettingsResponse.json();
      const apiSettingsList = window.kitsuCore.deserialise(apiSettingsData).data;
      apiSettingsList.forEach(setting => {
        apiSettings[setting.key] = setting;
      });
    } else {
      console.error(`[Settings] '${apiSettingsUrl}' returned status 400, please check response`, apiSettingsResponse.json());
    }
  } catch (error) {
    console.error(`[Settings] Fetching '${apiSettingsUrl}' resulted with error! \n`, error);
  }
  return apiSettings;
}

/**
 * Returns boolean in case if passed element is in viewport or not.
 * Source: https://www.javascripttutorial.net/dom/css/check-if-an-element-is-visible-in-the-viewport/
 * When 'partiallyVisible = true' (default) then the function returns 'true'
 * when even the smallest part of the element is visible,
 * and if 'partiallyVisible = false' then the entire element must be in the viewport
 * for the function to return 'true'.
*/
window.isInViewport = (el, partiallyVisible = true) => {
  const {
    top,
    left,
    bottom,
    right
  } = el.getBoundingClientRect();
  const {
    innerHeight,
    innerWidth
  } = window;
  return partiallyVisible ? (top >= 0 && top <= innerHeight || bottom >= 0 && bottom <= innerHeight) && (left >= 0 && left <= innerWidth || right >= 0 && right <= innerWidth) : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};

/**
 * Sets and fetches data from API crucial for application functionalities.
 * Triggers `BaseDataLoaded` event at the end that can be used as `document.addEventListener`
 */
window.isBaseDataLoaded = false;
document.addEventListener('DOMContentLoaded', async () => {
  window.apiSettings = await getApiSettings();
  Object.freeze(window.apiSettings);
  document.dispatchEvent(new Event('BaseDataLoaded'));
  window.isBaseDataLoaded = true;
});

/**
 * Return the setting value for the given setting key
 */
window.getSettingsFromApi = settingKey => window.apiSettings?.[settingKey]?.value || false;
window.baseHeadersJsonApi = {
  'Content-Type': 'application/vnd.api+json',
  'Accept': 'application/vnd.api+json'
};

/**
 * Information about the logged in user
 */
let cachedUserProfile = null;
window.getProfileData = () => {
  if (cachedUserProfile) return cachedUserProfile;
  if (window.getUserToken()) {
    cachedUserProfile = JSON.parse(localStorage.getItem('profileData'));
    return cachedUserProfile;
  }
  return null;
};

/**
 * Information about whether the user is premium
 */
window.isUserPremium = () => window.getProfileData()?.piano_subscription_type === 'subscriber';

/**
 * Generate user PPPID value that is used on Google Ads (DFP) and other Google services.
 */
window.getUserPPID = userId => window.md5(`googletag-PPID-${userId}`);

/**
 * Used as 'cont_type' value in DFP slots and as `custom_taxonomy` in `sendUpscoreData`
 */
window.dfpMap = {
  'longform': 'longarticle',
  'gallery': 'gallery',
  'live': 'livearticle',
  'comments': 'comments',
  'enigmatika': 'enigmatika',
  'tags': 'tags',
  'video': 'video_feed',
  'section': 'section',
  'subsection': 'sub_sec',
  'special': 'special',
  'homepage': 'homepage',
  'authors': 'authors'
};
window.getDfpContentType = articleType => window.dfpMap[articleType] || 'article';

/**
 * Information about the Midas card types
 */
window.midasCardTypesMap = {
  'pr': 1,
  'tes': 2,
  'video': 3,
  'dedicated': 4,
  'native': 5,
  'tesspec': 6
};

/**
 * Part of the URL for retrieving Midas cards
 */
window.MIDAS_URL = 'https://api.midas-network.com/Widget/Index3Async/';
const currentPlatform = window.getPlatform();
window.Cookies.set('client_platform', currentPlatform);
window.addEventListener('resize', window.debounce(() => {
  const newPlatform = window.getPlatform();
  if (newPlatform !== currentPlatform) {
    if (window.showGeneralLoader) window.showGeneralLoader();
    if (window.Cookies) window.Cookies.set('client_platform', newPlatform);
    window.location.reload();
  }
}, 200));

/**
 * A function used on article, comment, and subcomment pages
 */
window.toggleCommentText = button => {
  const commentDiv = button.parentNode;
  const hiddenText = commentDiv.querySelector('.comment-hidden-text');
  if (hiddenText) {
    hiddenText.hidden = !hiddenText.hidden;
    button.hidden = !hiddenText.hidden;
  }
};

/**
 * This is used on the SSR homepage for the weather and horoscope widgets.
 * This covers both situations so that the same thing
 * doesn't need to be written twice.
 * ToDo: adjust 'if' condition before releasing SSR homepage as official!
 */
if (window.location.pathname === '/') {
  document.addEventListener('click', event => {
    if (!event.target.classList.contains('dropdown-content') && !event.target.classList.contains('dropdown-item') && event.target.dataset.excludeFromClickEventListener !== 'true') {
      const elementsToClose = document.querySelectorAll('.dropdown.is-active');
      elementsToClose.forEach(elem => {
        elem.classList.remove('is-active');
      });
      const html = document.documentElement;
      if (window.isMob && html && html.style.overflow === 'hidden') html.style.overflow = 'auto';
    }
  });
}
function modalHide() {
  const modal = document.querySelector('.modal');
  if (modal) modal.classList.remove('is-active');
}
function toggleHtmlOverflow() {
  const html = document.documentElement;
  if (html) html.style.overflow = html.style.overflow === 'hidden' ? 'auto' : 'hidden';
}
function setActiveOrNot(element) {
  element.classList.toggle('is-active');
  if (window.isMob) toggleHtmlOverflow();
}

/**
 * Checks if current location has `success_url` query and if it is
 * eligible for redirect purposes
 */
window.getSuccessUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const successUrl = urlParams.get('success_url');
  return successUrl && (successUrl.startsWith('https://') || successUrl.startsWith('//')) ? successUrl : null;
};

/**
 * Adds a class indicating that this is a plus+ user
 * which is later used within CSS to hide banner placeholders.
 */
window.setPremiumClass = () => {
  const userData = window.getProfileData();
  if (userData && userData.piano_subscription_type === 'subscriber') {
    const feedSection = document.querySelector('.site_wrap');
    if (feedSection) feedSection.classList.add('premium_user');
  }
};